import { Download } from 'lucide-react';
import * as React from 'react';

import { DimeButton } from '../DimeButton.js';
import {
  DimeDropdownMenu,
  DimeDropdownMenuContent,
  DimeDropdownMenuItem,
  DimeDropdownMenuTrigger,
} from '../DimeDropdownMenu.js';
import { DimeFilterButtonContent } from '../DimeFilterButtonContent.js';
import { DimeSpinner } from '../DimeSpinner.js';
import { buildCsv } from './build-csv.js';
import type { ExportRowsRequest } from './DimeDataTable.types.js';
import { downloadBlob } from './download-blob.js';
import type { ExportTable } from './export-table.js';
import { resolveExportColumns } from './resolve-export-columns.js';

export interface DataTableExportProps<TData> {
  table: ExportTable<TData>;
  fileName?: string;
  // Renders the trigger disabled with this tooltip; permission is the caller's decision.
  disabledReason?: string;
  // Resolves the full filtered dataset, or null to abort; absent, the loaded rows are exported.
  onRequestExportRows?: (options?: ExportRowsRequest) => Promise<TData[] | null>;
}

type ExportFormat = 'csv' | 'xlsx';

export function DataTableExport<TData>({
  table,
  fileName = 'export',
  disabledReason,
  onRequestExportRows,
}: Readonly<DataTableExportProps<TData>>) {
  const [isExporting, setIsExporting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Tracks the in-flight export's controller so an unmount can cancel every remaining page fetch.
  const abortControllerRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const columns = resolveExportColumns(table);
  const loadedRows = table.getRowModel().rows.map((row) => row.original);
  const selectedIds = table.getSelectedRowIds?.() ?? [];
  const rowIdOf = table.getRowId;

  // The rows an export should write (decision 15).
  async function resolveExportRows(signal: AbortSignal): Promise<TData[] | null> {
    if (selectedIds.length === 0 || !rowIdOf) {
      return onRequestExportRows ? await onRequestExportRows({ signal }) : loadedRows;
    }

    const wanted = new Set(selectedIds);
    const selectedFromLoaded = loadedRows.filter((row, index) => wanted.has(rowIdOf(row, index)));
    // Every selected record is already in hand -- the common case, and it must stay offline.
    if (selectedFromLoaded.length === wanted.size) return selectedFromLoaded;

    if (!onRequestExportRows) return selectedFromLoaded;
    const wholeDataset = await onRequestExportRows({ selectedRowCount: wanted.size, signal });
    if (wholeDataset === null) return null;
    return wholeDataset.filter((row, index) => wanted.has(rowIdOf(row, index)));
  }

  async function runExport(format: ExportFormat) {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsExporting(true);
    setError(null);
    try {
      const rows = await resolveExportRows(controller.signal);
      // The component may have unmounted while the fetch was in flight.
      if (controller.signal.aborted) return;
      // null is an abort, not an empty dataset, so no header-only file is written.
      if (rows === null) return;
      if (format === 'csv') {
        downloadBlob(
          new Blob([buildCsv(columns, rows)], { type: 'text/csv;charset=utf-8' }),
          `${fileName}.csv`,
        );
      } else {
        // Loaded only on the first xlsx export, so the writer stays out of the main bundle.
        const { buildXlsxBlob } = await import('./build-xlsx-blob.js');
        const blob = await buildXlsxBlob(columns, rows);
        // The component may have unmounted while the workbook was being built.
        if (controller.signal.aborted) return;
        downloadBlob(blob, `${fileName}.xlsx`);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Export failed. Please try again.');
    } finally {
      if (!controller.signal.aborted) setIsExporting(false);
    }
  }

  if (columns.length === 0) return null;
  if (loadedRows.length === 0 && !onRequestExportRows) return null;

  const isDisabled = isExporting || disabledReason !== undefined;

  const trigger = (
    <DimeButton
      type="button"
      variant="outline"
      size="sm"
      // Square once the label hides below sm, like an icon button.
      className="gap-1.5 max-sm:size-7 max-sm:p-0"
      disabled={isDisabled}
      disabledTooltip={disabledReason}
      // Keeps the spinner's own "Loading" label out of the button's accessible name.
      aria-label={isExporting ? 'Exporting…' : 'Export'}
    >
      <DimeFilterButtonContent
        label={isExporting ? 'Exporting…' : 'Export'}
        icon={
          isExporting ? (
            <DimeSpinner className="size-3.5" />
          ) : (
            <Download className="size-3.5" aria-hidden />
          )
        }
      />
    </DimeButton>
  );

  return (
    <div className="flex items-center gap-2">
      {error && (
        <span role="alert" className="text-sm text-destructive">
          {error}
        </span>
      )}
      {/* Outside the dropdown when disabled, or the tooltip span would still open the menu. */}
      {disabledReason === undefined ? (
        <DimeDropdownMenu>
          <DimeDropdownMenuTrigger asChild>{trigger}</DimeDropdownMenuTrigger>
          <DimeDropdownMenuContent align="end">
            <DimeDropdownMenuItem onSelect={() => void runExport('csv')}>CSV</DimeDropdownMenuItem>
            <DimeDropdownMenuItem onSelect={() => void runExport('xlsx')}>
              Excel (.xlsx)
            </DimeDropdownMenuItem>
          </DimeDropdownMenuContent>
        </DimeDropdownMenu>
      ) : (
        trigger
      )}
    </div>
  );
}
